import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

// Send auth cookies on every API call.
axios.defaults.withCredentials = true;
// An admin-forced reset can land mid-session: route the user to the password step.
axios.interceptors.response.use(undefined, (err) => {
  if (err?.response?.status === 403 && err.response.data?.error === "password_change_required"
      && window.location.pathname !== "/set-password") {
    window.location.assign("/set-password");
  }
  return Promise.reject(err);
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // user: null while unknown, object when authenticated, false when not.
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
    } catch {
      try {
        const r2 = await axios.post(`${API}/auth/refresh`);
        setUser(r2.data.user);
      } catch {
        setUser(false);
      }
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      setUser(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // best-effort, clear local state regardless
    }
    setUser(false);
  };

  // Entitlement helpers, modes: "none" | "preview" | "full"
  const featureMode = useCallback(
    (key) => (user && user.entitlements ? user.entitlements[key] || "none" : "none"),
    [user]
  );
  const hasFeature = useCallback((key) => featureMode(key) === "full", [featureMode]);

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, setUser, hasFeature, featureMode }}>
      {children}
    </AuthContext.Provider>
  );
}
